/**
 * ReceivedGiftsPage - View audio gifts received by the user
 *
 * Displays a list of gift sessions sent to the current user,
 * with sender info, gift messages, and audio playback.
 *
 * Supports embedded mode for use within the unified GiftsPage.
 */

import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Gift, Heart, Play, Download, Inbox, Loader2, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import EnhancedAudioPlayer from '@/components/EnhancedAudioPlayer';
import { format } from 'date-fns';
import { useReceivedGifts, type ReceivedGift } from '@/hooks/useReceivedGifts';

interface ReceivedGiftsPageProps {
  embedded?: boolean;
}

const ReceivedGiftsPage = ({ embedded = false }: ReceivedGiftsPageProps) => {
  const navigate = useNavigate();
  const [expandedGift, setExpandedGift] = useState<string | null>(null);

  const {
    gifts: receivedGifts,
    isLoading,
    error,
    markAsViewed,
  } = useReceivedGifts();

  // Handle expanding a gift and marking it as viewed
  const handleExpandGift = (gift: ReceivedGift) => {
    setExpandedGift(gift.id);
    // Mark as viewed if not already viewed
    if (!gift.gift_viewed) {
      markAsViewed(gift.id);
    }
  };

  if (isLoading) {
    const loadingContent = (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );

    if (embedded) return loadingContent;
    return (
      <div className="container max-w-4xl mx-auto px-4 py-8">
        {loadingContent}
      </div>
    );
  }

  if (error) {
    const errorContent = (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-destructive mb-4">Failed to load received gifts</p>
          <Button onClick={() => navigate('/app')}>Go Home</Button>
        </div>
      </div>
    );

    if (embedded) return errorContent;
    return (
      <div className="container max-w-4xl mx-auto px-4 py-8">
        {errorContent}
      </div>
    );
  }

  const content = (
    <>
      {receivedGifts.length === 0 ? (
        <Card className="text-center py-16">
          <CardContent>
            <Inbox className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">No Gifts Yet</h2>
            <p className="text-muted-foreground mb-6">
              When someone sends you an audio gift, it will appear here.
            </p>
            <Button onClick={() => navigate('/app')}>
              Explore VoiceThera
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {receivedGifts.map((gift) => (
            <Card
              key={gift.id}
              className="overflow-hidden border-pink-100 dark:border-pink-900/30"
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Heart className="h-4 w-4 text-pink-500 fill-pink-500" />
                      <span className="text-sm text-muted-foreground">
                        From: <strong>{gift.sender_name || 'Someone special'}</strong>
                      </span>
                      {!gift.gift_viewed && (
                        <Badge variant="secondary" className="ml-2 text-xs bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300">
                          <Sparkles className="h-3 w-3 mr-1" />
                          New
                        </Badge>
                      )}
                    </div>
                    <h3 className="text-xl font-bold">{gift.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Received {format(new Date(gift.created_at), 'MMMM d, yyyy')}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-sm text-muted-foreground">
                      {gift.duration ? `${Math.round(gift.duration / 60)} min` : ''}
                    </span>
                  </div>
                </div>

                {gift.gift_message && (
                  <div className="bg-pink-50 dark:bg-pink-950/30 p-4 rounded-lg mb-4">
                    <p className="text-muted-foreground italic">
                      "{gift.gift_message}"
                    </p>
                  </div>
                )}

                {expandedGift === gift.id ? (
                  <div className="space-y-4">
                    <EnhancedAudioPlayer
                      audioUrl={gift.audio_url}
                      sessionTitle={gift.title}
                      backgroundMusicFile={gift.background_music_file || undefined}
                    />
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = gift.audio_url;
                          link.download = `${gift.title}.mp3`;
                          link.click();
                        }}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedGift(null)}
                      >
                        Collapse
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    onClick={() => handleExpandGift(gift)}
                    className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Play Gift
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );

  // When embedded, return just the content without container/header
  if (embedded) {
    return content;
  }

  // Standalone mode with container and header
  return (
    <div className="container max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="flex items-center justify-center gap-3 mb-4">
          <Gift className="h-10 w-10 text-pink-500" />
          <h1 className="text-4xl font-bold">Received Gifts</h1>
        </div>
        <p className="text-muted-foreground text-lg">
          Audio gifts that have been sent to you
        </p>
      </div>

      {content}
    </div>
  );
};

export default ReceivedGiftsPage;
